import { Percomatic } from '@biggby-coffee/percomatic-typescript';

import { CustomerOrderInput, GiftCardPaymentInput } from '../../../graphql';
import { defined } from '../../../utils/defined';
import { moneyToCents } from '../../../utils/moneyToCents';
import { transItemToOrderItemInput } from '../../../utils/transItemToOrderItemInput';
import { State } from '../../types';
import { isCarInput } from '../isCarInput';
import { isCustomerOrderInput } from '../isCustomerOrderInput';

export const createCustomerOrderInput = (
	state: State,
	transaction: Percomatic.Serialized.Transaction,
	stripeIntentId: string | undefined,
	profileId: number | undefined,
	appUserId?: number | undefined
): CustomerOrderInput => {
	const output: CustomerOrderInput = {
		pickupLocation: state.pickupLocation,
		subtotal: moneyToCents(transaction.subTotal),
		taxAmount: moneyToCents(transaction.taxAmount),
		total: moneyToCents(transaction.total),
		items: transaction.items
			.filter(item => item.isSent)
			.map(transItemToOrderItemInput),
		payment: {
			giftCardPayments: calculateGiftCardPayments(transaction, state),
			creditCardPayment: defined(stripeIntentId)
				? {
						amount: moneyToCents(state.balanceDue?.toString()),
						token: stripeIntentId
					}
				: undefined
		}
	};

	output.guestEmail = state.guestEmail;
	output.pickupName = state.pickupName;
	output.phone = state.phone;
	output.appUserId = appUserId;
	output.profileId = profileId;

	if (state.isScheduled) {
		output.time = state.time;
	}

	if (state.pickupLocation === 'PARKING_LOT') {
		const car = {
			year: state.carYear,
			color: state.carColor,
			make: state.carMake,
			model: state.carModel
		};

		if (!isCarInput(car)) {
			throw new Error('Cannot create a valid car from the input state.');
		}

		output.car = car;
	}

	if (!isCustomerOrderInput(output)) {
		throw new Error(
			'Cannot create a valid customer order from the input state.'
		);
	}

	return output;
};

const calculateGiftCardPayments = (
	transaction: Percomatic.Serialized.Transaction,
	state: State
): GiftCardPaymentInput[] => {
	const totalAmountToCharge = moneyToCents(transaction.total.toString());
	let remainingAmountToCharge = totalAmountToCharge;

	const sortedGiftCardPayments = state.giftCardPayments
		.map(card => ({
			amount: moneyToCents(card.amount?.toString()),
			cardNumber: card.cardNumber
		}))
		.sort((a, b) => a.amount - b.amount);

	const updatedGiftCardPayments = sortedGiftCardPayments
		.map(card => {
			if (remainingAmountToCharge <= 0) {
				return undefined;
			}

			const amountToCharge = Math.min(
				card.amount,
				remainingAmountToCharge
			);
			remainingAmountToCharge -= amountToCharge;

			return {
				...card,
				amount: amountToCharge
			};
		})
		.filter(defined);

	return updatedGiftCardPayments;
};
